import { getCookie, setCookie } from 'tiny-cookie'
import 'formdata-polyfill'

const Consent = {
  el: document.querySelector('.cookie-policy'),

  getState({ keyname }) {
    return getCookie(keyname)
  },

  setState({ keyname, state }) {
    setCookie(keyname, JSON.stringify(state))

    return state
  },

  updateState({ consentItems, formData = false }) {
    const state = []

    consentItems.map(item => (
      state.push({
        consent: item.consent || false,
        purpose: item.purpose || '',
        name: item.name,
        slug: Consent.slugify({ text: item.name }),
      })
    ))

    // for all initial consent items compare with new form data consent results
    // and update consent as necessary
    if (formData) {
      for (const oldItem of state) {
        for (const newItem of formData.entries()) {
          if (Consent.slugify({ text: newItem[0] }) === oldItem.slug) {
            oldItem.consent = newItem[1] === 'on'
          }
        }
      }
    }

    return state
  },

  slugify({ text }) {
    return text
      .toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/[^\w-]+/g, '') // Remove all non-word chars
      .replace(/--+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, '')
  },

  setUI({ state }) {
    const contentMarkup = state.reduce((accu, val, idx) => {
      const slug = Consent.slugify({ text: val.name })

      return (
        `${accu}
        <p class="consent__heading">
          <label class="consent__label" for="frm-consent-${idx}">${val.name}</label>
          <input class="consent__input" type="checkbox" name="${slug}" id="frm-consent-${idx}" checked="true">
          <button class="consent__button t-para" aria-expanded="${idx !== 0}" aria-controls="consent-collapsible-${idx}">
            ${val.name}
          </button>
        </p>
        <div class="consent__copy" id="consent-collapsible-${idx}" aria-hidden="${idx !== 0}">
            <p>${val.purpose}</p>
        </div>`
      )
    }, '')

    const uiMarkup = `<div class="consent js-consent" tabindex="-1">
                        <dialog class="consent__outer" aria-describedby="consentDescription" aria-labelledby="consentHeading" role="dialog">
                          <label class="consent__label" for="frm-close">Close</label>
                          <div class="consent__inner">
                            <h1 class="consent__general-heading" id="consentHeading">Cookie Policy</h1>
                            <p class="consent__general-copy" id="consentDescription">
                              We use cookies to enhance your experience of this website. We also use them to analyse traffic to the site (which we anonymise) so that we can improve our online store. Cookies also help us remember your preferences (such as location) and share information with our social media, advertising and analytics partners. For more information, view our <a href="/pages/privacy-policy#cookie-policy" alt="Cookie Policy" target="_blank">Cookie Policy</a>.
                            </p>
                            <form class="consent__form" method="POST" accept-charset="UTF-8">
                            <div class="consent__content">
                              ${contentMarkup}
                            </div>
                            <label class="consent__label" for="frm-submit">Submit</label>
                            <input class="c-btn consent__submit" type="button" value="Continue" id="frm-submit">
                            </form>
                            <div class="consent__inner-bg"></div>
                          </div>
                        </dialog>
                      </div>`

    document.body.insertAdjacentHTML('beforeend', uiMarkup)

    return uiMarkup
  },

  setUIHandlers({ keyname, state, callback }) {
    document.body.addEventListener('click', (e) => {
      // expand buttons
      if (e.target.classList.contains('consent__button')) {
        e.preventDefault()

        const button = e.target
        const heading = button.parentElement
        const copy = heading.nextElementSibling

        const expanded = button.getAttribute('aria-expanded') === 'true' ? 'false' : 'true'
        const hidden = copy.getAttribute('aria-hidden') === 'true' ? 'false' : 'true'

        button.setAttribute('aria-expanded', expanded)
        copy.setAttribute('aria-hidden', hidden)
      }

      // submit button
      if (e.target.classList.contains('consent__submit')) {
        e.preventDefault()

        const form = e.target.parentElement
        const formData = new FormData(form)
        const component = document.querySelector('.consent')

        // update consent state with new form data
        const newConsentState = Consent.updateState({ consentItems: state, formData })

        // set storage with new consent state
        Consent.setState({ keyname, state: newConsentState })

        // hide ui
        component.classList.remove('consent--active')
        document.documentElement.classList.remove('modal-is-open')

        // if a callback was specified execute now passing new consent state
        if (callback && typeof callback === 'function') {
          callback(newConsentState)
        }
      }

      // close button
      if (e.target.classList.contains('consent__close')) {
        e.preventDefault()

        const form = e.target.parentElement
        const formData = new FormData(form)
        const component = form.parentElement

        // update consent state with new form data
        const newConsentState = Consent.updateState({ consentItems: state, formData })

        // set storage with new consent state
        Consent.setState({ keyname, state })

        // hide ui
        component.classList.remove('consent--active')
        document.documentElement.classList.remove('modal-is-open')

        // if a callback was specified execute now passing new consent state
        if (callback && typeof callback === 'function') {
          callback(newConsentState)
        }
      }
    })
  },

  openConsent() {
    const cookiePolicy = document.querySelector(Consent.ui.selectors.cookiePolicy)
    const consent = document.querySelector(Consent.ui.selectors.consent)

    consent.classList.add(Consent.ui.classes.isActive)
    document.documentElement.classList.add('modal-is-open')

    // hide cookie banner
    cookiePolicy.classList.remove(Consent.ui.classes.policyIsActive)

    // set cookie so banner doesn't appear again
    setCookie('seedlip-cookie-policy', true)
  },

  closeCookiePolicy(callback, keyname, consentItems) {
    const cookiePolicy = document.querySelector(Consent.ui.selectors.cookiePolicy)
    const state = Consent.updateState({ consentItems })
    const form = document.querySelector('.consent__form')
    const formData = new FormData(form)

    // update consent state with new form data
    const newConsentState = Consent.updateState({ consentItems: state, formData })

    // set storage with new consent state
    Consent.setState({ keyname, state: newConsentState })

    // if a callback was specified execute now passing new consent state
    if (callback && typeof callback === 'function') {
      callback(newConsentState)
    }

    // hide cookie banner
    cookiePolicy.classList.remove(Consent.ui.classes.policyIsActive)

    // set cookie so banner doesn't appear again
    setCookie('seedlip-cookie-policy', true)
  },

  initialise(callback, keyname = 'seedlip-consent', consentItems) {
    const cookiePolicy = document.querySelector(Consent.ui.selectors.cookiePolicy)
    const cookiePolicySeen = !!getCookie('seedlip-cookie-policy')
    const consentSeen = !!getCookie('seedlip-consent')

    if (cookiePolicy && !cookiePolicySeen && !consentSeen) {
      const manageButton = cookiePolicy.querySelector(Consent.ui.selectors.openManageCookies)
      const closeButton = cookiePolicy.querySelector(Consent.ui.selectors.closeCookiesPolicy)

      // show cookie banner
      cookiePolicy.classList.add(Consent.ui.classes.policyIsActive)

      // add cookie banner event listeners
      manageButton.addEventListener('click', Consent.openConsent)
      closeButton.addEventListener('click', () => Consent.closeCookiePolicy(callback, keyname, consentItems))
    }
  },

  enable({ callback, keyname = 'seedlip-consent', consentItems = [] }) {
    // get storage contents if it already exists
    const currentConsentState = Consent.getState({ keyname })

    // if a value has already been set and there is a valid callback specified
    //   TRUE: execute the callback passing the current conset state object
    //   ELSE: if options have been specified - create consent state, update ui, and show ui
    if (currentConsentState && callback && typeof callback === 'function') {
      callback(JSON.parse(currentConsentState))
    } else if (consentItems.length > 0) {
      const state = Consent.updateState({ consentItems })
      Consent.setUI({ state })
      Consent.setUIHandlers({ keyname, state, callback })
    }

    Consent.initialise(callback, keyname, consentItems)
  },

  ui: {
    classes: {
      isActive: 'consent--active',
      policyIsActive: 'cookie-policy--is-active',
    },
    selectors: {
      consent: '.js-consent',
      cookiePolicy: '.js-cookie-policy',
      openManageCookies: '.js-consent__manage-button',
      closeCookiesPolicy: '.js-consent__close-button',
    },
  },
}

export default { enable: Consent.enable }
